import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client, { formatApiError, BACKEND_ORIGIN } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { toast } from "sonner";
import {
  Users, Search, PackageCheck, CheckCircle2, FileText, X, LogOut,
  Phone, User as UserIcon, Wrench, Clock, ClipboardList, AlertTriangle, ChevronRight,
} from "lucide-react";

const RESOLUTIONS = [
  { key: "correct_component_provided", label: "Correct Component Provided" },
  { key: "component_replaced",         label: "Component Replaced" },
  { key: "order_component_updated",    label: "Order Component Updated" },
  { key: "approved_alternative",       label: "Approved Alternative Component" },
  { key: "other",                      label: "Other" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function fmtBytes(n) {
  if (!n) return "";
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
}

/**
 * SALES TEAM dashboard.
 *
 * Scope:
 *  - Ready for Pickup / Picked Up tabs (search by #, name, full phone)
 *  - Mark as Picked Up
 *  - View Invoice
 *  - Component Issues (view + resolve; resolution returns component to PENDING)
 *
 * DOES NOT touch: streaming, LivePlayer, OBS, Cloudflare, WhatsApp, Component
 * Verification logic, station configuration, environment, or the build
 * lifecycle. Pickup is a separate fulfillment state.
 */
export default function SalesDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("ready"); // ready | picked_up | issues
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);      // order for detail
  const [pickModal, setPickModal] = useState(null);    // order to confirm pickup
  const [busy, setBusy] = useState(false);

  // Component Issues state (tab === "issues")
  const [issueTab, setIssueTab] = useState("open"); // open | resolved
  const [issues, setIssues] = useState([]);
  const [issueToResolve, setIssueToResolve] = useState(null);

  const doLogout = () => { logout(); nav("/sales/login"); };

  const loadPickups = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/sales/pickups`, {
        params: { tab, q: q.trim() || undefined, limit: 300 },
      });
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load pickups");
    } finally { setLoading(false); }
  }, [tab, q]);

  const loadIssues = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/admin/component-issues`, {
        params: { status: issueTab === "open" ? "OPEN" : "RESOLVED", limit: 200 },
      });
      setIssues(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load issues");
    } finally { setLoading(false); }
  }, [issueTab]);

  useEffect(() => {
    if (tab === "issues") { loadIssues(); }
    else { loadPickups(); }
  }, [tab, loadPickups, loadIssues]);

  // Auto-refresh
  useEffect(() => {
    const id = setInterval(() => {
      if (tab === "issues") loadIssues(); else loadPickups();
    }, 15000);
    return () => clearInterval(id);
  }, [tab, loadPickups, loadIssues]);

  const openInvoice = (order) => {
    // Open in a new tab. Backend requires Bearer JWT — we use fetch + blob to
    // preserve authentication (window.open cannot attach the header).
    const url = `${BACKEND_ORIGIN}/api/orders/${order.id}/invoice/binary`;
    const t = localStorage.getItem("idea_token");
    fetch(url, { headers: { Authorization: `Bearer ${t}` } })
      .then(async (r) => {
        if (r.status === 404) throw new Error("NO INVOICE AVAILABLE");
        if (!r.ok) throw new Error(`Failed (${r.status})`);
        const blob = await r.blob();
        const objurl = URL.createObjectURL(blob);
        const w = window.open(objurl, "_blank", "noopener,noreferrer");
        if (!w) {
          // fallback: navigate current tab
          const a = document.createElement("a");
          a.href = objurl; a.target = "_blank"; a.rel = "noopener";
          a.download = order.invoice_filename || `invoice-${order.order_number || order.id}`;
          document.body.appendChild(a); a.click(); a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(objurl), 60000);
      })
      .catch((e) => toast.error(e.message || "Failed to open invoice"));
  };

  const confirmPickup = async () => {
    if (!pickModal) return;
    setBusy(true);
    try {
      await client.post(`/sales/orders/${pickModal.id}/pickup`, {});
      toast.success(`Order #${pickModal.order_number} marked as PICKED UP`);
      setPickModal(null);
      setSelected(null);
      loadPickups();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Pickup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "#050507" }}>
      {/* Top bar */}
      <div
        className="sticky top-0 z-20 border-b"
        style={{ borderColor: "var(--border)", background: "rgba(5,5,5,0.85)", backdropFilter: "blur(14px)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <BrandMark subtitle="SALES TEAM" small />
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/50 hidden md:inline">{user?.email}</span>
            <button
              onClick={doLogout}
              data-testid="sales-logout"
              className="text-white/60 hover:text-white text-xs inline-flex items-center gap-1.5"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-white/50">Handover</div>
            <h1 className="mt-1 text-2xl font-semibold text-white flex items-center gap-2">
              <Users size={20} style={{ color: "var(--accent)" }} /> Sales Team
            </h1>
          </div>
        </div>

        {/* Main tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <TabBtn active={tab === "ready"} onClick={() => setTab("ready")} testid="tab-ready" icon={PackageCheck}>Ready for Pickup</TabBtn>
          <TabBtn active={tab === "picked_up"} onClick={() => setTab("picked_up")} testid="tab-picked-up" icon={CheckCircle2}>Picked Up</TabBtn>
          <TabBtn active={tab === "issues"} onClick={() => setTab("issues")} testid="tab-issues" icon={AlertTriangle}>Component Issues</TabBtn>
        </div>

        {tab !== "issues" && (
          <>
            {/* Search */}
            <div className="mb-5 flex items-center gap-2 max-w-xl">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") loadPickups(); }}
                  placeholder="Search by order #, name, or phone number"
                  className="w-full pl-9 pr-3 h-10 rounded-md text-sm text-white outline-none"
                  style={{ background: "#0d0d10", border: "1px solid rgba(255,255,255,0.08)" }}
                  data-testid="sales-search-input"
                />
              </div>
              <button
                onClick={loadPickups}
                className="h-10 px-4 rounded-md text-[11px] font-semibold tracking-[0.18em] uppercase"
                style={{ background: "rgba(0,229,255,0.10)", color: "var(--accent)", border: "1px solid rgba(0,229,255,0.30)" }}
                data-testid="sales-search-btn"
              >Search</button>
            </div>

            {loading && <div className="text-white/40 py-6" data-testid="sales-loading">Loading…</div>}

            {!loading && rows.length === 0 && (
              <div className="rounded-xl border p-10 text-center text-white/45" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }} data-testid="sales-empty">
                {tab === "ready"
                  ? "No completed orders waiting for pickup."
                  : "No orders have been handed over yet."}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3" data-testid="sales-orders-list">
              {rows.map((o) => (
                <OrderRow
                  key={o.id}
                  order={o}
                  onOpen={() => setSelected(o)}
                  onPickup={() => setPickModal(o)}
                  onViewInvoice={() => openInvoice(o)}
                />
              ))}
            </div>
          </>
        )}

        {tab === "issues" && (
          <IssuesPanel
            issueTab={issueTab}
            setIssueTab={setIssueTab}
            issues={issues}
            loading={loading}
            onResolve={setIssueToResolve}
          />
        )}
      </div>

      {/* Detail Drawer */}
      <OrderDetailDrawer
        order={selected}
        onClose={() => setSelected(null)}
        onPickup={() => { if (selected) setPickModal(selected); }}
        onViewInvoice={() => selected && openInvoice(selected)}
      />

      {/* Confirm Pickup Modal */}
      <ConfirmPickupModal
        open={!!pickModal}
        order={pickModal}
        busy={busy}
        onCancel={() => setPickModal(null)}
        onConfirm={confirmPickup}
      />

      {/* Resolve Issue Modal */}
      <ResolveIssueModal
        open={!!issueToResolve}
        issue={issueToResolve}
        onClose={() => setIssueToResolve(null)}
        onResolved={loadIssues}
      />
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-2 px-4 h-10 rounded-md text-[11px] font-semibold tracking-[0.22em] uppercase transition-colors"
      style={{
        background: active ? "rgba(0,229,255,0.10)" : "transparent",
        color:      active ? "var(--accent)" : "rgba(255,255,255,0.55)",
        border:     `1px solid ${active ? "rgba(0,229,255,0.30)" : "rgba(255,255,255,0.08)"}`,
      }}
    >
      <Icon size={13} /> {children}
    </button>
  );
}

function OrderRow({ order, onOpen, onPickup, onViewInvoice }) {
  const isPicked = order.pickup_status === "PICKED_UP";
  return (
    <div
      className="rounded-xl border p-4 md:p-5"
      style={{
        borderColor: isPicked ? "rgba(0,255,102,0.28)" : "rgba(0,229,255,0.24)",
        background: "#0d0d10",
      }}
      data-testid={`pickup-row-${order.order_number || order.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div
              className="text-[10px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-md"
              style={{
                background: isPicked ? "rgba(0,255,102,0.14)" : "rgba(0,229,255,0.10)",
                color: isPicked ? "#00ff88" : "var(--accent)",
              }}
              data-testid={`pickup-status-${order.order_number}`}
            >{isPicked ? "PICKED UP" : "READY FOR PICKUP"}</div>
            <div className="text-white text-lg font-semibold font-mono">#{order.order_number || "—"}</div>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <MiniField icon={UserIcon} label="Customer" value={order.customer_name || "—"} />
            <MiniField icon={Phone}    label="Phone"    value={order.customer_whatsapp || "—"} testid={`pickup-phone-${order.order_number}`} />
            <MiniField icon={Wrench}   label="Technician" value={order.technician_name || "—"} />
            <MiniField icon={ClipboardList} label="Station" value={order.station_id || "—"} />
            <MiniField icon={Clock}    label={isPicked ? "Picked Up" : "Completed"} value={fmtDate(isPicked ? order.picked_up_at : order.finish_time)} className="col-span-2" />
            {isPicked && order.picked_up_by_name && (
              <MiniField icon={UserIcon} label="Handed Over By" value={order.picked_up_by_name} className="col-span-2" />
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onViewInvoice}
            disabled={!order.has_invoice}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.18em] uppercase px-3 py-2 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "rgba(255,255,255,0.04)", color: "#e5e7eb", border: "1px solid rgba(255,255,255,0.10)" }}
            title={order.has_invoice ? "View Invoice" : "NO INVOICE AVAILABLE"}
            data-testid={`pickup-view-invoice-${order.order_number}`}
          >
            <FileText size={12} /> {order.has_invoice ? "View Invoice" : "No Invoice"}
          </button>
          {!isPicked && (
            <button
              type="button"
              onClick={onPickup}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.18em] uppercase px-3 py-2 rounded-md"
              style={{ background: "rgba(0,255,102,0.10)", color: "#00ff88", border: "1px solid rgba(0,255,102,0.30)" }}
              data-testid={`mark-picked-up-btn-${order.order_number}`}
            >
              <CheckCircle2 size={12} /> Mark As Picked Up
            </button>
          )}
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.18em] uppercase px-3 py-2 rounded-md text-white/70 hover:text-white"
            style={{ border: "1px solid rgba(255,255,255,0.10)" }}
            data-testid={`pickup-open-detail-${order.order_number}`}
          >
            Details <ChevronRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniField({ icon: Icon, label, value, className = "", testid }) {
  return (
    <div className={className} data-testid={testid}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 font-semibold flex items-center gap-1"><Icon size={10} /> {label}</div>
      <div className="text-white text-sm mt-0.5 break-all">{value}</div>
    </div>
  );
}

function OrderDetailDrawer({ order, onClose, onPickup, onViewInvoice }) {
  if (!order) return null;
  const isPicked = order.pickup_status === "PICKED_UP";
  return (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-end"
      style={{ background: "rgba(0,0,0,0.62)" }}
      onClick={onClose}
      data-testid="order-detail-drawer"
    >
      <div
        className="w-full max-w-md h-full border-l overflow-y-auto"
        style={{ background: "#0a0a0d", borderColor: "rgba(255,255,255,0.08)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between p-5 border-b" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0a0a0d" }}>
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase text-white/45 font-semibold">Order</div>
            <div className="mt-0.5 text-white text-lg font-semibold font-mono">#{order.order_number || "—"}</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md flex items-center justify-center text-white/60 hover:text-white" style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid="drawer-close">
            <X size={14} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <Section title="Customer">
            <KV label="Full Name" value={order.customer_name || "—"} testid="drawer-customer-name" />
            <KV label="Phone / WhatsApp" value={order.customer_whatsapp || "—"} testid="drawer-customer-phone" />
          </Section>

          <Section title="Order">
            <KV label="Order Number" value={<span className="font-mono">#{order.order_number || "—"}</span>} />
            <KV label="Completion Date" value={fmtDate(order.finish_time)} />
            <KV label="Technician" value={order.technician_name || "—"} />
            <KV label="Station" value={order.station_id || "—"} />
          </Section>

          <Section title="Invoice">
            {order.has_invoice ? (
              <>
                <KV label="File" value={order.invoice_filename || "invoice"} />
                {order.invoice_size_bytes ? <KV label="Size" value={fmtBytes(order.invoice_size_bytes)} /> : null}
                <button
                  onClick={onViewInvoice}
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.18em] uppercase px-3 py-2 rounded-md"
                  style={{ background: "rgba(0,229,255,0.10)", color: "var(--accent)", border: "1px solid rgba(0,229,255,0.30)" }}
                  data-testid="drawer-view-invoice"
                >
                  <FileText size={12} /> View Invoice
                </button>
              </>
            ) : (
              <div className="text-white/40 text-sm italic" data-testid="drawer-no-invoice">NO INVOICE AVAILABLE</div>
            )}
          </Section>

          <Section title="Handover">
            {!isPicked ? (
              <>
                <div className="text-[10px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-md inline-block" style={{ background: "rgba(0,229,255,0.10)", color: "var(--accent)" }}>
                  READY FOR PICKUP
                </div>
                <button
                  onClick={onPickup}
                  className="mt-3 inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase px-4 py-2 rounded-md"
                  style={{ background: "rgba(0,255,102,0.10)", color: "#00ff88", border: "1px solid rgba(0,255,102,0.30)" }}
                  data-testid="drawer-mark-picked-up"
                >
                  <CheckCircle2 size={12} /> Mark As Picked Up
                </button>
              </>
            ) : (
              <>
                <div className="text-[10px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-md inline-block" style={{ background: "rgba(0,255,102,0.14)", color: "#00ff88" }}>
                  ✓ PICKED UP
                </div>
                <div className="mt-2">
                  <KV label="Picked Up At" value={fmtDate(order.picked_up_at)} />
                  <KV label="Picked Up By" value={order.picked_up_by_name || "—"} />
                </div>
              </>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border p-4" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0d0d10" }}>
      <div className="text-[10px] tracking-[0.24em] uppercase text-white/45 font-semibold mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ label, value, testid }) {
  return (
    <div className="flex items-start gap-3 py-0.5" data-testid={testid}>
      <div className="text-[11px] tracking-[0.14em] uppercase text-white/45 min-w-[110px]">{label}</div>
      <div className="text-white text-sm break-all">{value}</div>
    </div>
  );
}

function ConfirmPickupModal({ open, order, busy, onCancel, onConfirm }) {
  if (!open || !order) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.72)" }} onClick={onCancel} data-testid="confirm-pickup-modal">
      <div className="rounded-xl border max-w-md w-full" style={{ background: "#0d0d10", borderColor: "rgba(0,255,102,0.35)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 flex items-center gap-2.5 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <CheckCircle2 size={16} style={{ color: "#00ff88" }} />
          <div className="text-white text-sm font-semibold tracking-[0.22em] uppercase">Confirm Customer Pickup</div>
        </div>
        <div className="px-5 py-5 space-y-3">
          <div className="text-white text-sm">
            Order <span className="font-mono font-semibold">#{order.order_number}</span>
          </div>
          <div className="text-white/60 text-sm">
            Confirm that this PC has been handed over to the customer?
          </div>
          <div className="rounded-md px-3 py-2 text-sm" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="text-white">{order.customer_name || "—"}</div>
            <div className="text-white/60 text-xs mt-0.5">{order.customer_whatsapp || ""}</div>
          </div>
        </div>
        <div className="px-5 py-4 flex items-center justify-end gap-2 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <button type="button" onClick={onCancel} className="px-4 py-2 text-xs font-semibold tracking-[0.18em] uppercase text-white/60 hover:text-white" data-testid="pickup-cancel">Cancel</button>
          <button type="button" disabled={busy} onClick={onConfirm} className="px-4 py-2 rounded-md text-xs font-semibold tracking-[0.18em] uppercase disabled:opacity-30" style={{ background: "rgba(0,255,102,0.9)", color: "#00251a" }} data-testid="pickup-confirm">
            {busy ? "Saving…" : "Confirm Pickup"}
          </button>
        </div>
      </div>
    </div>
  );
}

function IssuesPanel({ issueTab, setIssueTab, issues, loading, onResolve }) {
  return (
    <>
      <div className="mb-4 inline-flex rounded-md border overflow-hidden" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
        {[{k:"open",l:"Open"},{k:"resolved",l:"Resolved"}].map((t) => (
          <button
            key={t.k}
            onClick={() => setIssueTab(t.k)}
            className="px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-semibold"
            style={{
              background: issueTab === t.k ? "rgba(0,229,255,0.10)" : "transparent",
              color:      issueTab === t.k ? "var(--accent)" : "rgba(255,255,255,0.55)",
            }}
            data-testid={`sales-issue-tab-${t.k}`}
          >{t.l}</button>
        ))}
      </div>

      {loading && <div className="text-white/40 py-6" data-testid="sales-issues-loading">Loading…</div>}

      {!loading && issues.length === 0 && (
        <div className="rounded-xl border p-10 text-center text-white/45" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }} data-testid="sales-issues-empty">
          {issueTab === "open" ? "No open component issues. 🎉" : "No resolved issues yet."}
        </div>
      )}

      <div className="space-y-3" data-testid="sales-issues-list">
        {issues.map((r) => (
          <div key={r.id} className="rounded-xl border p-4 md:p-5" style={{ borderColor: r.status === "OPEN" ? "rgba(255,152,0,0.28)" : "rgba(255,255,255,0.08)", background: "#0d0d10" }} data-testid={`sales-issue-row-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="text-[10px] tracking-[0.22em] uppercase font-semibold px-2 py-0.5 rounded-md" style={{
                    background: r.status === "OPEN" ? "rgba(255,152,0,0.15)" : "rgba(0,255,136,0.12)",
                    color: r.status === "OPEN" ? "#ffb857" : "#00ff88",
                  }}>{r.status}</div>
                  <div className="text-white/80 text-sm font-semibold">{r.reason_label}</div>
                </div>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <MiniField icon={ClipboardList} label="Order" value={<span className="font-mono">#{r.order_number}</span>} />
                  <MiniField icon={ClipboardList} label="Station" value={r.station_id || "—"} />
                  <MiniField icon={Wrench} label="Technician" value={r.reported_by_name || "—"} />
                  <MiniField icon={Clock} label="Reported" value={fmtDate(r.reported_at)} />
                  <MiniField icon={FileText} label="Component" value={r.component_label} className="col-span-2" />
                  <MiniField icon={FileText} label="Expected" value={r.expected_value || "—"} className="col-span-2" />
                </div>
                {r.notes && (
                  <div className="mt-3 rounded-md px-3 py-2 text-sm text-white/80 italic" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>"{r.notes}"</div>
                )}
                {r.status === "RESOLVED" && (
                  <div className="mt-3 rounded-md px-3 py-2 text-sm" style={{ background: "rgba(0,255,136,0.05)", border: "1px solid rgba(0,255,136,0.18)" }}>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 font-semibold">Resolution</div>
                    <div className="text-white mt-0.5">{r.resolution_type_label || "—"}</div>
                    {r.resolution_notes && <div className="text-white/70 italic mt-1">"{r.resolution_notes}"</div>}
                    <div className="text-white/45 text-[11px] mt-1">Resolved by {r.resolved_by_name || "—"} · {fmtDate(r.resolved_at)}</div>
                  </div>
                )}
              </div>
              {r.status === "OPEN" && (
                <button
                  type="button"
                  onClick={() => onResolve(r)}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.18em] uppercase px-3 py-2 rounded-md"
                  style={{ background: "rgba(0,229,255,0.10)", color: "var(--accent)", border: "1px solid rgba(0,229,255,0.30)" }}
                  data-testid={`sales-resolve-btn-${r.id}`}
                >
                  <CheckCircle2 size={12} /> Resolve
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function ResolveIssueModal({ open, issue, onClose, onResolved }) {
  const [type, setType] = useState("correct_component_provided");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setType("correct_component_provided"); setNotes(""); setBusy(false); }
  }, [open]);

  if (!open || !issue) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await client.post(`/admin/component-issues/${issue.id}/resolve`, {
        resolution_type: type,
        resolution_notes: notes.trim() || null,
      });
      toast.success("Issue resolved. Technician must re-verify component.");
      onResolved && onResolved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Resolve failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.72)" }} onClick={onClose} data-testid="sales-resolve-issue-modal">
      <div className="rounded-xl border max-w-md w-full max-h-[92vh] overflow-y-auto" style={{ background: "#0d0d10", borderColor: "rgba(0,229,255,0.35)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 flex items-center gap-2.5 border-b" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <CheckCircle2 size={16} style={{ color: "#00e5ff" }} />
          <div className="text-white text-sm font-semibold tracking-[0.22em] uppercase">Resolve Issue</div>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold">Component</div>
            <div className="mt-1 text-white text-sm font-semibold">{issue.component_label}</div>
            <div className="text-white/60 text-sm">{issue.expected_value}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold mb-2">Resolution</div>
            <div className="grid grid-cols-1 gap-1.5">
              {RESOLUTIONS.map((r) => (
                <label key={r.key} className="flex items-center gap-2.5 p-2.5 rounded-md cursor-pointer border" style={{
                  borderColor: type === r.key ? "rgba(0,229,255,0.45)" : "rgba(255,255,255,0.06)",
                  background:  type === r.key ? "rgba(0,229,255,0.08)" : "transparent",
                }} data-testid={`sales-resolution-${r.key}`}>
                  <input type="radio" name="sales-resolution-type" checked={type === r.key} onChange={() => setType(r.key)} className="accent-cyan-400" />
                  <span className="text-white text-sm">{r.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 font-semibold mb-1.5">Sales Notes</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={500} placeholder="Optional notes for the audit trail." className="w-full rounded-md px-3 py-2 text-sm text-white outline-none" style={{ background: "#050507", border: "1px solid rgba(255,255,255,0.1)" }} data-testid="sales-resolution-notes-input"/>
          </div>
        </div>
        <div className="px-5 py-4 flex items-center justify-end gap-2 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
          <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-semibold tracking-[0.18em] uppercase text-white/60 hover:text-white" data-testid="sales-resolve-cancel">Cancel</button>
          <button type="button" disabled={busy} onClick={submit} className="px-4 py-2 rounded-md text-xs font-semibold tracking-[0.18em] uppercase disabled:opacity-30" style={{ background: "rgba(0,229,255,0.9)", color: "#00252d" }} data-testid="sales-resolve-submit">
            {busy ? "Resolving…" : "Mark as Resolved"}
          </button>
        </div>
      </div>
    </div>
  );
}
